/**
 * EpisodesPullUpSheet — Pocket-FM style bottom sheet that surfaces the
 * episodes list for the series currently playing in the Player.
 *
 * Spec (Jun 5 2026 user screenshot):
 *   • Tabs: "EPISODES N / TOTAL"  |  "NEXT SERIES"
 *   • Episodes list scrolls; the currently-playing episode is auto-
 *     scrolled into view and highlighted PURPLE.
 *   • Tap an episode → navigates to /player/{episode_id} with autoplay
 *     (handed off to the same global AudioPlayer so playback continues
 *     seamlessly).
 *   • "Next series" tab shows lightweight series recommendations
 *     (writer's other series first, then platform-curated).
 *
 * The sheet itself is mounted by Player.jsx with `open` controlled by a
 * `setEpisodesOpen(true)` button (the small grab-handle bar at the
 * bottom of the player). Closing is via swipe-down or backdrop tap.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Play, CheckCircle2, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import WriterName from './WriterName';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TAB_EPISODES = 'episodes';
const TAB_NEXT = 'next';

const cleanTitle = (title, num) => {
  if (!title) return `Episode ${num || ''}`.trim();
  // Strip leading "E12. " / "Ep 12 — " patterns so the row shows the real title.
  return String(title)
    .replace(/^E[Pp]?\.?\s*\d+\s*[.\-—:]?\s*/i, '')
    .trim() || `Episode ${num || ''}`.trim();
};

const EpisodesPullUpSheet = ({
  open,
  onClose,
  seriesId,
  seriesTitle,
  currentEpisodeId,
  onPickEpisode,
}) => {
  const [tab, setTab] = useState(TAB_EPISODES);
  const [episodes, setEpisodes] = useState([]);
  const [nextSeries, setNextSeries] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const activeRowRef = useRef(null);

  // Fetch the series payload once when the sheet first opens for a given
  // series. We reuse `/api/library` (already cached most places) and pick
  // out the matching series — same flow SeriesPage uses, so the response
  // shape is guaranteed identical.
  useEffect(() => {
    if (!open || !seriesId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [libRes, progRes] = await Promise.all([
          authFetch(`${BACKEND_URL}/api/library`),
          authFetch(`${BACKEND_URL}/api/progress`),
        ]);
        if (libRes.ok && !cancelled) {
          const lib = await libRes.json();
          const allSeries = lib.series || [];
          const me = allSeries.find(
            (s) => (s.series_id || s.book_id) === seriesId,
          );
          setEpisodes((me?.episodes || []).slice().sort(
            (a, b) =>
              (a.episode_number || a.chapter_number || 0) -
              (b.episode_number || b.chapter_number || 0),
          ));

          // Build "Next series" recs: rest of the library minus this one,
          // capped to keep the sheet snappy.
          setNextSeries(
            allSeries
              .filter((s) => (s.series_id || s.book_id) !== seriesId)
              .slice(0, 25),
          );
        }
        if (progRes.ok && !cancelled) {
          const body = await progRes.json();
          const arr = Array.isArray(body) ? body : (body.progress || []);
          const m = {};
          arr.forEach((p) => {
            if (p.book_id) {
              m[p.book_id] = {
                completed: !!p.completed,
                pct: Math.max(0, Math.min(100, Math.round((p.progress || 0) * 100))),
              };
            }
          });
          setProgressMap(m);
        }
      } catch { /* non-fatal — sheet just shows empty state */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, seriesId]);

  // Auto-scroll the active episode into view whenever the sheet opens
  // or the playing episode changes.
  useEffect(() => {
    if (!open || tab !== TAB_EPISODES) return;
    const el = activeRowRef.current;
    if (el) {
      // Small delay lets the sheet finish its slide-up transition first
      // so the scroll lands accurately.
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 220);
      return () => clearTimeout(t);
    }
  }, [open, tab, currentEpisodeId, episodes.length]);

  const total = episodes.length;
  const currentIndex = useMemo(() => {
    const idx = episodes.findIndex(
      (e) => (e.episode_id || e.chapter_id || e.book_id) === currentEpisodeId,
    );
    return idx >= 0 ? idx + 1 : 0;
  }, [episodes, currentEpisodeId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end"
      data-testid="player-episodes-sheet"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        data-testid="player-episodes-sheet-backdrop"
      />
      <div
        className="relative w-full max-h-[85vh] bg-[#0b0a14] border-t border-purple-500/30 rounded-t-2xl shadow-2xl flex flex-col"
        style={{ animation: 'sheetSlideUp 260ms cubic-bezier(.2,.8,.2,1)' }}
      >
        <style>{`@keyframes sheetSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        {/* Grab handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1.5 rounded-full bg-white/30" />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between px-4 pt-2 pb-3 border-b border-white/5">
          <button
            type="button"
            onClick={() => setTab(TAB_EPISODES)}
            className={`flex items-center gap-2 text-[13px] font-black tracking-widest uppercase transition focus:outline-none ${
              tab === TAB_EPISODES ? 'text-white' : 'text-white/40'
            }`}
            data-testid="episodes-sheet-tab-episodes"
          >
            EPISODES
            {total > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-bold tabular-nums">
                {currentIndex || 1}<span className="text-white/40">/{total.toLocaleString()}</span>
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab(TAB_NEXT)}
            className={`text-[13px] font-black tracking-widest uppercase transition focus:outline-none ${
              tab === TAB_NEXT ? 'text-white' : 'text-white/40'
            }`}
            data-testid="episodes-sheet-tab-next"
          >
            NEXT SERIES
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full text-white/70 hover:text-white hover:bg-white/5 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
            data-testid="episodes-sheet-close"
            aria-label="Close episodes sheet"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-2 py-2"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
        >
          {loading && (
            <div className="flex items-center justify-center py-12 text-white/60">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          )}

          {!loading && tab === TAB_EPISODES && (
            <ul className="space-y-1.5">
              {episodes.length === 0 && (
                <li className="text-center text-white/40 py-10 text-sm">No episodes yet.</li>
              )}
              {episodes.map((ep, idx) => {
                const epId = ep.episode_id || ep.chapter_id || ep.book_id;
                const epNum = ep.episode_number || ep.chapter_number || idx + 1;
                const isActive = epId === currentEpisodeId;
                const prog = progressMap[epId];
                return (
                  <li
                    key={epId}
                    ref={isActive ? activeRowRef : null}
                  >
                    <button
                      type="button"
                      onClick={() => onPickEpisode?.(epId)}
                      className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 ${
                        isActive
                          ? 'bg-purple-600/25 border border-purple-400/60 shadow-[0_0_0_1px_rgba(168,85,247,0.25),0_8px_24px_-12px_rgba(168,85,247,0.5)]'
                          : 'border border-transparent hover:bg-white/[0.04]'
                      }`}
                      data-testid={`episodes-sheet-row-${epId}`}
                    >
                      {/* Episode number bubble */}
                      <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-[11px] font-black tabular-nums ${
                        isActive
                          ? 'bg-purple-500 text-white'
                          : prog?.completed
                            ? 'bg-emerald-600/30 text-emerald-200'
                            : 'bg-white/10 text-white/70'
                      }`}>
                        {isActive ? <Play className="w-4 h-4 fill-white" /> : prog?.completed ? <CheckCircle2 className="w-4 h-4" /> : `E${epNum}`}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-bold truncate ${isActive ? 'text-purple-100' : 'text-white/90'}`}>
                          {cleanTitle(ep.title, epNum)}
                        </p>
                        <p className="text-[11px] text-white/40 truncate">
                          E{epNum}{ep.duration ? ` · ${ep.duration}` : ep.duration_seconds ? ` · ${Math.max(1, Math.round(ep.duration_seconds / 60))} min` : ''}
                          {prog && !prog.completed && prog.pct > 0 ? ` · ${prog.pct}%` : ''}
                        </p>
                      </div>

                      {isActive && (
                        <span className="shrink-0 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-purple-500 text-white">
                          Now playing
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && tab === TAB_NEXT && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-1">
              {nextSeries.length === 0 && (
                <li className="text-center text-white/40 py-10 text-sm col-span-full">
                  Nothing else to suggest right now.
                </li>
              )}
              {nextSeries.map((s) => {
                const sid = s.series_id || s.book_id;
                const cover = resolveCoverUrl(
                  s.series_cover_image
                    || s.series_cover_image_url
                    || s.cover_image
                    || s.cover_image_url
                    || (s.episodes && s.episodes[0]?.cover_image),
                );
                return (
                  <li key={sid}>
                    <button
                      type="button"
                      onClick={() => {
                        onClose?.();
                        // Tiny defer so close animation kicks in before nav.
                        setTimeout(() => {
                          window.location.assign(`/series/${sid}`);
                        }, 80);
                      }}
                      className="w-full flex items-center gap-3 rounded-xl border border-white/10 hover:border-purple-400/50 bg-white/[0.03] hover:bg-white/[0.06] p-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 text-left"
                      data-testid={`episodes-sheet-next-${sid}`}
                    >
                      <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-slate-800 ring-1 ring-white/10">
                        {cover ? (
                          <img src={cover} alt="" className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-white truncate">{s.series_title || s.title || 'Untitled'}</p>
                        <p className="text-[11px] text-white/50 truncate">
                          {(s.episodes?.length || 0).toLocaleString()} ep ·{' '}
                          <WriterName
                            userId={s.writer_user_id || s.user_id || s.author_user_id}
                            name={s.writer_name || s.author || ''}
                            stopPropagation
                            className="text-white/70"
                          />
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default EpisodesPullUpSheet;
